// src/components/GeneralErrorBoundry.tsx
import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { logger } from "../lib/logger";
import { ErrorCodes } from "../lib/errorCodes";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function DefaultErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Typography variant="h6" color="error">
        Something went wrong
      </Typography>
      <Typography variant="body2" color="text.secondary">
        An unexpected error occurred. You can try again or refresh the page.
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="contained" sx={{ borderRadius: "50px" }} onClick={resetErrorBoundary}>
          Try again
        </Button>
        <Button
          variant="outlined"
          sx={{ borderRadius: "50px" }}
          onClick={() => window.location.reload()}
        >
          Refresh page
        </Button>
      </Box>
    </Box>
  );
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("ui", ErrorCodes.UI_001, "React error boundary caught a crash", error, {
      componentStack: errorInfo.componentStack,
    });
  }

  resetErrorBoundary = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.FallbackComponent ?? DefaultErrorFallback;
      return <Fallback error={this.state.error!} resetErrorBoundary={this.resetErrorBoundary} />;
    }

    return this.props.children;
  }
}

export { DefaultErrorFallback };
